import { e } from '@frontend/shared/src/utils/e';
import { TIME } from '@shared/src/utils/time';
import { decline_dynamic_word } from '@shared/src/utils/decline_word';
import { Countdown } from '@frontend/auction/src/helpers/Countdown';
import { decline } from '@shared/src/utils/decline';
import { WallOfShameDialogUserSettings } from './wall-of-shame-dialog-user-settings/wall-of-shame-dialog-user-settings';
import type { WOSAPIUser } from '../../services/WallOfShameService';

const ATTR = 'wall-of-shame-user';

// const get_diff_detailed = (timestamp: number) => {
//   const diff = Math.max(0, timestamp - Date.now());

//   if (diff <= 0) {
//     return { days: 0, hours: 0, minutes: 0, seconds: 0 };
//   }

//   let left = diff;
//   const days_left = Math.floor(left / TIME.DAY);
//   left = left % TIME.DAY;

//   const hours_left = Math.floor(left / TIME.HOUR);
//   left = left % TIME.HOUR;

//   const minutes_left = Math.floor(left / TIME.MINUTE);
//   left = left % TIME.MINUTE;

//   const seconds_left = Math.floor(left / TIME.SECOND);

//   return {
//     days: days_left,
//     hours: hours_left,
//     minutes: minutes_left,
//     seconds: seconds_left,
//   };
// };

const banned_since_info = (banned_since: number) => {
  const how_long_days = Math.floor((Date.now() - banned_since) / TIME.DAY);

  return {
    date: new Date(banned_since).toLocaleDateString('RU-ru'),
    how_long: `(${how_long_days} ${decline_dynamic_word(['день', 'дня', 'дней'], how_long_days)})`,
  };
};

export class WallOfShameUser extends HTMLDivElement {
  static from_api = (data: WOSAPIUser) => {
    const element = new WallOfShameUser();

    element.user_id = data.id;
    element.name = data.name;
    element.reason = data.reason;
    element.unban_ts = data.unban_ts;
    element.banned_since = data.banned_since;

    return element;
  };

  #remove_element!: HTMLElement;
  #edit_element!: HTMLElement;

  #name_element!: HTMLElement;
  #reason_container_element!: HTMLElement;
  #reason_element!: HTMLElement;

  #unban_after_element!: HTMLElement;
  #unban_after_days_element!: HTMLElement;
  #unban_after_hours_element!: HTMLElement;
  #unban_after_minutes_element!: HTMLElement;
  #unban_after_seconds_element!: HTMLElement;

  #banned_since_element!: HTMLElement;
  #banned_since_date_element!: HTMLElement;
  #banned_since_how_long_element!: HTMLElement;

  #user_id: number = 0;

  #name: string = '';
  #reason: string = '';
  #unban_ts: number = 0;
  #banned_since_ts: number = 0;

  #countdown = new Countdown({ time_left: 0, step: TIME.SECOND });
  timeout: number | null = null;
  interval: number | null = null;

  get user_id() {
    return this.#user_id;
  }
  set user_id(value) {
    this.#user_id = value;
  }

  get name() {
    return this.#name;
  }
  set name(value) {
    this.#name = value;

    if (this.#name_element) {
      this.#name_element.innerText = this.#name;
    }
  }

  get reason() {
    return this.#reason;
  }
  set reason(value) {
    this.#reason = value;

    if (this.#reason_element) {
      this.#reason_element.innerText = this.#reason;
    }
  }

  get unban_ts() {
    return this.#unban_ts;
  }
  set unban_ts(value) {
    this.#unban_ts = value;

    const left = this.#unban_ts - Date.now();

    if (this.#unban_after_element) {
      this.#update_timer(left);
    }

    this.#countdown.set_time(left);
  }

  get banned_since() {
    return this.#banned_since_ts;
  }
  set banned_since(value) {
    this.#banned_since_ts = value;

    if (this.#banned_since_date_element && this.#banned_since_how_long_element) {
      const _banned_since_info = banned_since_info(this.#banned_since_ts);

      this.#banned_since_date_element.innerText = _banned_since_info.date;
      this.#banned_since_how_long_element.innerText = _banned_since_info.how_long;
    }
  }

  constructor() {
    super();

    this.setAttribute('is', ATTR);

    new ResizeObserver(() => {
      // this.style.setProperty('--user-width', String(this.clientWidth));
      this.style.setProperty('--user-height', String(this.clientHeight));
    }).observe(this);
  }

  connectedCallback() {
    this.#edit_element =
      this.querySelector<HTMLDivElement>('.edit') ??
      e.div({ className: 'edit' }, [e.icon('edit', 24)]);

    this.#remove_element =
      this.querySelector<HTMLDivElement>('.remove') ??
      e.div({ className: 'remove' }, [e.icon('cross', 24)]);

    this.#name_element =
      this.querySelector<HTMLDivElement>('.name') ?? e.div({ className: 'name' });

    this.#reason_element =
      this.querySelector<HTMLDivElement>('.reason') ?? e.div({ className: 'reason no-scrollbar' });

    this.#reason_container_element =
      this.querySelector<HTMLDivElement>('.reason_container') ??
      e.div({ className: 'reason_container' }, [this.#reason_element]);

    this.#unban_after_element =
      this.querySelector<HTMLDivElement>('.unban_after') ??
      e.div({ className: 'unban_after' }, [
        e.div({ className: 'days' }),
        e.div({ className: 'hours' }),
        e.div({ className: 'minutes' }),
        e.div({ className: 'seconds' }),
      ]);

    this.#unban_after_days_element =
      this.#unban_after_element.querySelector('.days') ?? e.div({ className: 'days' });
    this.#unban_after_hours_element =
      this.#unban_after_element.querySelector('.hours') ?? e.div({ className: 'hours' });
    this.#unban_after_minutes_element =
      this.#unban_after_element.querySelector('.minutes') ?? e.div({ className: 'minutes' });
    this.#unban_after_seconds_element =
      this.#unban_after_element.querySelector('.seconds') ?? e.div({ className: 'seconds' });

    this.#banned_since_element =
      this.querySelector<HTMLDivElement>('.banned_since') ??
      e.div({ className: 'banned_since' }, [
        e.div({ className: 'date' }),
        e.div({ className: 'how_long' }),
      ]);

    this.#banned_since_date_element =
      this.#banned_since_element.querySelector('.date') ?? e.div({ className: 'date' });
    this.#banned_since_how_long_element =
      this.#banned_since_element.querySelector('.how_long') ?? e.div({ className: 'how_long' });

    this.user_id = this.#user_id;
    this.name = this.#name;
    this.reason = this.#reason;
    this.unban_ts = this.#unban_ts;
    this.banned_since = this.#banned_since_ts;

    this.#edit_element.addEventListener('click', () => {
      document.body.append(WallOfShameDialogUserSettings.from_user(this));
    });

    this.append(
      this.#edit_element,
      this.#remove_element,
      this.#name_element,
      this.#reason_container_element,
      this.#unban_after_element,
      this.#banned_since_element
    );

    this.#countdown.on_time_change(this.#update_timer);
    this.#countdown.start();
  }

  #update_timer = (diff: number) => {
    if (diff <= 0) {
      this.#update_days(0);
      this.#update_hours(0);
      this.#update_minutes(0);
      this.#update_seconds(0);

      return;
    }

    let left = diff;
    const days_left = Math.floor(left / TIME.DAY);
    left = left % TIME.DAY;

    const hours_left = Math.floor(left / TIME.HOUR);
    left = left % TIME.HOUR;

    const minutes_left = Math.floor(left / TIME.MINUTE);
    left = left % TIME.MINUTE;

    const seconds_left = Math.floor(left / TIME.SECOND);

    this.#update_days(days_left);
    this.#update_hours(hours_left);
    this.#update_minutes(minutes_left);
    this.#update_seconds(seconds_left);
  };

  #update_days = (value: number) => {
    const as_string = value.toString();
    if (this.#unban_after_days_element.innerHTML !== as_string) {
      this.#unban_after_days_element.innerHTML = as_string;
      this.#unban_after_days_element.dataset.text = decline(['день', 'дня', 'дней'], value);
      this.dataset.d = as_string;
    }
  };

  #update_hours = (value: number) => {
    const as_string = value.toString();
    if (this.#unban_after_hours_element.innerHTML !== as_string) {
      this.#unban_after_hours_element.innerHTML = as_string;
      this.#unban_after_hours_element.dataset.text = decline(['час', 'часа', 'часов'], value);
      this.dataset.h = as_string;
    }
  };

  #update_minutes = (value: number) => {
    const as_string = value.toString();
    if (this.#unban_after_minutes_element.innerHTML !== as_string) {
      this.#unban_after_minutes_element.innerHTML = as_string;
      this.#unban_after_minutes_element.dataset.text = decline(
        ['минута', 'минуты', 'минут'],
        value
      );
      this.dataset.m = as_string;
    }
  };

  #update_seconds = (value: number) => {
    const as_string = value.toString();
    if (this.#unban_after_seconds_element.innerHTML !== as_string) {
      this.#unban_after_seconds_element.innerHTML = as_string;
      this.#unban_after_seconds_element.dataset.text = decline(
        ['секунда', 'секунды', 'секунд'],
        value
      );
      this.dataset.s = as_string;
    }
  };
}

customElements.whenDefined(ATTR).then(() => console.log('[DEFINED]', ATTR));
customElements.define(ATTR, WallOfShameUser, { extends: 'div' });
